"use client";

import { useEffect } from "react";
import { captureException } from "@miadvg/posthog";
import { Button } from "@/components/ui/button";

interface ErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

/**
 * Route-level error boundary
 *
 * Catches errors in route segments and displays a fallback UI.
 * Automatically captures the exception to PostHog for error tracking.
 *
 * @see https://nextjs.org/docs/app/building-your-application/routing/error-handling
 */
export default function Error({ error, reset }: ErrorProps) {
  useEffect(() => {
    // Capture the exception to PostHog
    captureException(error, {
      extra: { digest: error.digest },
    });
  }, [error]);

  return (
    <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 p-4">
      <h1 className="text-2xl font-semibold tracking-tight">
        Something went wrong
      </h1>
      <p className="text-muted-foreground text-sm">
        An unexpected error occurred. Please try again.
      </p>
      <Button
        onClick={reset}
        className="transition-transform active:scale-[0.96]"
      >
        Try again
      </Button>
    </div>
  );
}
